// Interactive globe — agent network visualisation for the Hero.
// Pure 2D canvas projection, no Three.js. Drag to rotate; idle = auto-spin.
import { useRef, useEffect, useCallback } from "react";

const AGENT_MARKERS = [
  { lat: 45,  lng: -30,  label: "Scribe" },
  { lat: 20,  lng: 60,   label: "Examiner" },
  { lat: -20, lng: 20,   label: "Regis" },
  { lat: -40, lng: -60,  label: "Conductor" },
  { lat: 60,  lng: 80,   label: "Sentinel" },
  { lat: 10,  lng: -100, label: "Herald" },
];

const AGENT_CONNECTIONS = [
  { from: [45, -30],  to: [20, 60]   },
  { from: [45, -30],  to: [-20, 20]  },
  { from: [20, 60],   to: [-40, -60] },
  { from: [-20, 20],  to: [-40, -60] },
  { from: [60, 80],   to: [45, -30]  },
  { from: [60, 80],   to: [20, 60]   },
  { from: [-40, -60], to: [10, -100] },
];

function latLngToXYZ(lat, lng, r) {
  const phi = ((90 - lat) * Math.PI) / 180;
  const theta = ((lng + 180) * Math.PI) / 180;
  return [
    -(r * Math.sin(phi) * Math.cos(theta)),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta),
  ];
}
function rotY(x, y, z, a) { const c = Math.cos(a), s = Math.sin(a); return [x*c+z*s, y, -x*s+z*c]; }
function rotX(x, y, z, a) { const c = Math.cos(a), s = Math.sin(a); return [x, y*c-z*s, y*s+z*c]; }
function proj(x, y, z, cx, cy, fov) { const sc = fov/(fov+z); return [x*sc+cx, y*sc+cy, z]; }

export default function InteractiveGlobe({ size = 480 }) {
  const canvasRef = useRef(null);
  const rotYRef = useRef(0.4);
  const rotXRef = useRef(0.2);
  const dragRef = useRef({ active: false, sx: 0, sy: 0, sry: 0, srx: 0 });
  const animRef = useRef(null);
  const timeRef = useRef(0);
  const dotsRef = useRef([]);

  useEffect(() => {
    const dots = [];
    const N = 900;
    const phi = (1 + Math.sqrt(5)) / 2;
    for (let i = 0; i < N; i++) {
      const theta = 2 * Math.PI * i / phi;
      const p = Math.acos(1 - 2 * (i + 0.5) / N);
      dots.push([Math.cos(theta)*Math.sin(p), Math.cos(p), Math.sin(theta)*Math.sin(p)]);
    }
    dotsRef.current = dots;
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);
    const cx = w / 2, cy = h / 2;
    const R = Math.min(w, h) * 0.40;
    const fov = 500;

    if (!dragRef.current.active) rotYRef.current += 0.002;
    timeRef.current += 0.012;
    const time = timeRef.current;
    ctx.clearRect(0, 0, w, h);

    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(255,255,255,0.12)";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    const ry = rotYRef.current, rx = rotXRef.current;

    for (const dot of dotsRef.current) {
      let [x, y, z] = [dot[0]*R, dot[1]*R, dot[2]*R];
      [x, y, z] = rotX(x, y, z, rx);
      [x, y, z] = rotY(x, y, z, ry);
      if (z > 0) continue;
      const [sx, sy] = proj(x, y, z, cx, cy, fov);
      const da = Math.max(0.15, 1 - (z + R) / (2.5 * R));
      const ds = 1.0 + da * 1.0;
      // Glow halo
      ctx.beginPath();
      ctx.arc(sx, sy, ds * 2.8, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${(da * 0.06).toFixed(2)})`;
      ctx.fill();
      // Main dot
      ctx.beginPath();
      ctx.arc(sx, sy, ds, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${(da * 0.55).toFixed(2)})`;
      ctx.fill();
    }

    for (const conn of AGENT_CONNECTIONS) {
      let [x1, y1, z1] = latLngToXYZ(conn.from[0], conn.from[1], R);
      let [x2, y2, z2] = latLngToXYZ(conn.to[0], conn.to[1], R);
      [x1, y1, z1] = rotX(x1, y1, z1, rx); [x1, y1, z1] = rotY(x1, y1, z1, ry);
      [x2, y2, z2] = rotX(x2, y2, z2, rx); [x2, y2, z2] = rotY(x2, y2, z2, ry);
      if (z1 > R * 0.3 && z2 > R * 0.3) continue;
      const [sx1, sy1] = proj(x1, y1, z1, cx, cy, fov);
      const [sx2, sy2] = proj(x2, y2, z2, cx, cy, fov);
      const mx = (x1+x2)/2, my = (y1+y2)/2, mz = (z1+z2)/2;
      const ml = Math.sqrt(mx*mx + my*my + mz*mz);
      const ah = R * 1.22;
      const [scx, scy] = proj((mx/ml)*ah, (my/ml)*ah, (mz/ml)*ah, cx, cy, fov);
      ctx.beginPath();
      ctx.moveTo(sx1, sy1);
      ctx.quadraticCurveTo(scx, scy, sx2, sy2);
      ctx.strokeStyle = "rgba(255,255,255,0.15)";
      ctx.lineWidth = 0.8;
      ctx.stroke();
      const t = (Math.sin(time * 1.1 + conn.from[0] * 0.1) + 1) / 2;
      const tx = (1-t)*(1-t)*sx1 + 2*(1-t)*t*scx + t*t*sx2;
      const ty = (1-t)*(1-t)*sy1 + 2*(1-t)*t*scy + t*t*sy2;
      ctx.beginPath();
      ctx.arc(tx, ty, 1.8, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.fill();
    }

    for (const m of AGENT_MARKERS) {
      let [x, y, z] = latLngToXYZ(m.lat, m.lng, R);
      [x, y, z] = rotX(x, y, z, rx);
      [x, y, z] = rotY(x, y, z, ry);
      if (z > R * 0.1) continue;
      const [sx, sy] = proj(x, y, z, cx, cy, fov);
      const pulse = (Math.sin(time * 2 + m.lat * 0.1) * 0.5 + 0.5);
      ctx.beginPath();
      ctx.arc(sx, sy, 4 + pulse * 5, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255,255,255,${(0.08 + pulse * 0.12).toFixed(2)})`;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(sx, sy, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.95)";
      ctx.fill();
      ctx.font = "600 11px 'DM Sans', sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.fillText(m.label, sx + 10, sy + 4);
    }

    animRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [draw]);

  const onPointerDown = (e) => {
    dragRef.current = { active: true, sx: e.clientX, sy: e.clientY, sry: rotYRef.current, srx: rotXRef.current };
    try { e.target.setPointerCapture(e.pointerId); } catch { /* no-op */ }
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    rotYRef.current = dragRef.current.sry + (e.clientX - dragRef.current.sx) * 0.005;
    rotXRef.current = Math.max(-1, Math.min(1, dragRef.current.srx + (e.clientY - dragRef.current.sy) * 0.005));
  };
  const onPointerUp = () => { dragRef.current.active = false; };

  return (
    <canvas
      ref={canvasRef}
      data-testid="hero-globe"
      style={{ width: size, height: size, cursor: "grab", touchAction: "none" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    />
  );
}
